// Fonte única dos cards de cashback e da paleta deles. Consumido pelo
// CashbackMenu (menu lateral, desktop + bottom sheet) e pelo CashbackSheet
// (Clube Fidelidade), que antes duplicavam a lista e os gradientes.
//
// Pra mudar porcentagem, ordem, habilitar/desabilitar um cashback ou trocar a
// cor de um card, mexa AQUI — os dois lugares acompanham.

export interface CashbackItemTheme {
  // Fundo do corpo do card.
  card?: string;
  // Cor do nome do jogo.
  cardText?: string;
  // Cor do rótulo "Cashback" acima do nome.
  accent?: string;
  // Fundo da pílula da porcentagem.
  pill?: string;
  // Cor do "Até {porcentagem}" dentro da pílula.
  pillText?: string;
}

export interface CashbackItemData {
  name: string;
  percent: string;
  img: string;
  // `false` deixa o card em grayscale/opacity (ainda clicável).
  enabled?: boolean;
  url?: string;
  // Sobrescreve só o que precisa da paleta padrão (CASHBACK_DEFAULT_THEME).
  theme?: CashbackItemTheme;
  // Texto do selo ao lado do nome (o chip dourado do header, ver
  // CashbackItem.vue). Sem valor, nenhum selo é desenhado.
  badge?: string;
}

// Geometria de luz compartilhada por TODOS os cards (vem do Figma): brilho no
// canto superior esquerdo. Qualquer paleta nova tem que usar este mesmo radial —
// um `linear-gradient` chapado faz o card parecer de outro conjunto, mesmo com a
// cor certa.
const cardGradient = (from: string, to: string) =>
  `radial-gradient(168.3% 138% at 36.76% -16.67%, ${from} 0%, ${to} 100%)`;

// Paleta padrão: corpo azul + pílula laranja.
export const CASHBACK_DEFAULT_THEME = {
  card: cardGradient('#1515E5', '#000098'),
  cardText: '#FFF',
  accent: '#00DDFF',
  pill: cardGradient('#FF704A', '#FF3400'),
  pillText: '#FFF',
} satisfies Required<CashbackItemTheme>;

// Cashback Ao Vivo: corpo âmbar onde os outros são azuis e pílula bronze onde os
// outros são laranja. Três notas de contraste: o texto do corpo vira escuro
// porque branco sobre dourado não se lê; o âmbar é saturado (não um champanhe
// pálido) pra ter o mesmo peso visual dos irmãos em vez de parecer desbotado; e
// o bronze da pílula é de valor médio, não quase-preto, pra ler como da mesma
// família do card e não como um recorte vazado nele.
const GOLD_THEME: CashbackItemTheme = {
  card: cardGradient('#FFC93F', '#96600C'),
  cardText: '#2A1B00',
  accent: '#6B4708',
  pill: cardGradient('#A0621C', '#552A06'),
};

export const CASHBACK_ITEMS: CashbackItemData[] = [
  {
    name: 'Slots',
    percent: '20%',
    img: '/assets/images/menu-lateral-grid/slots.png',
    url: '/promotions/cashback-diario-ativo',
  },
  {
    name: 'Aviator',
    percent: '20%',
    img: '/assets/images/menu-lateral-grid/aviator.png',
    url: '/promotions/cashback-aviator',
  },
  {
    name: 'Ao Vivo',
    percent: '10%',
    img: '/assets/images/menu-lateral-grid/ao-vivo.png',
    url: '/promotions/cashback-vip-jogos-ao-vivo',
    theme: GOLD_THEME,
    badge: 'VIP',
  },
  {
    name: 'Crash',
    percent: '20%',
    img: '/assets/images/menu-lateral-grid/crash.png',
    enabled: false,
    url: '/promotions/cashback-crash',
  },
];

// Fallback de navegação quando o item não traz `url`.
export const CASHBACK_FALLBACK_URL = '/promotions/cashback-diario-ativo';

// `/promotions/<slug>` → `<slug>`. O post é buscado pelo slug puro, e o
// `openPost` do store só normaliza o prefixo `blog/` — o de `promotions` sai
// aqui. Vive junto da lista porque tem dois consumidores: o CashbackSheet (hub)
// e o CashbackMenu do menu lateral (via CasinoItems).
export function cashbackPostSlug(url: string) {
  return url.replace(/^\/+/, '').replace(/^promotions\//, '');
}
